import React, { useState, useMemo, useRef } from 'react';
import { Plus, Search, Home, X, PackagePlus, Printer, Camera } from "lucide-react";
import CameraScanner from './CameraScanner';

const EMPTY_FORM = { id: "", item: "", status: "실병", locType: "warehouse", location: "", chargeExpiry: "" };
const DEFAULT_ITEMS = ["LPG 20kg", "LPG 50kg", "산소(O2)", "질소(N2)", "아르곤(Ar)"];


const InventoryPanel = ({ cylinders, setCylinders, customers = [], warehouses = [], setWarehouses, items = [], stockAdjustments = {} }) => {
  const CUSTOMERS = customers.map(c => c.name);
  // 품목 목록: Settings 품목 + cylinders에 있는 품목 합집합 (없으면 기본값)
  const GAS_ITEMS = useMemo(() => {
    const merged = [...new Set([...items.map(i => i.name), ...cylinders.map(c => c.item)].filter(Boolean))];
    return merged.length > 0 ? merged : DEFAULT_ITEMS;
  }, [items, cylinders]);
  const [filter, setFilter]         = useState("all"); // all | warehouse | customer
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModal, setIsAddModal] = useState(false);
  const [isWHModal, setIsWHModal]   = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [newWHName, setNewWHName]   = useState("");
  const [formError, setFormError]   = useState("");

  // 바코드 모달
  const [barcodeCyl, setBarcodeCyl] = useState(null);
  const barcodeRef = useRef(null);

  const handlePrintBarcode = (cyl) => {
    const svg = barcodeRef.current?.querySelector('svg');
    if (!svg) return;
    const win = window.open('', '_blank', 'width=440,height=340');
    win.document.write(`
      <html><head><title>바코드 - ${cyl.id}</title>
      <style>
        body { margin: 30px; font-family: sans-serif; text-align: center; }
        .info { font-size: 13px; color: #555; margin-top: 6px; }
        .id   { font-size: 15px; font-weight: 700; margin-top: 10px; font-family: monospace; }
      </style></head>
      <body>
        ${svg.outerHTML}
        <div class="id">${cyl.id}</div>
        <div class="info">${cyl.item} · ${cyl.location}</div>
        <script>window.onload = function(){ window.print(); window.close(); }</script>
      </body></html>
    `);
    win.document.close();
  };

  // 창고별 집계 (useMemo로 cylinders가 바뀔 때만 재계산)
  const warehouseStocks = useMemo(() => {
    const result = {};
    warehouses.forEach(wh => {
      result[wh] = {};
      GAS_ITEMS.forEach(item => { result[wh][item] = { 실병: 0, 공병: 0 }; });
    });
    cylinders.forEach(c => {
      if (c.locType === "warehouse" && result[c.location]) {
        if (!result[c.location][c.item]) {
          result[c.location][c.item] = { 실병: 0, 공병: 0 };
        }
        if (c.status === '실병' || c.status === '공병') {
          result[c.location][c.item][c.status]++;
        }
      }
    });
    // 이월재고에서 등록한 음수 조정치 반영 (실물 용기로 표현 불가능한 부족분)
    Object.entries(stockAdjustments).forEach(([key, adj]) => {
      const [locType, location, item, status] = key.split('|');
      if (locType === 'warehouse' && result[location] && (status === '실병' || status === '공병')) {
        if (!result[location][item]) result[location][item] = { 실병: 0, 공병: 0 };
        result[location][item][status] += adj;
      }
    });
    return result;
  }, [cylinders, warehouses, GAS_ITEMS, stockAdjustments]);

  // 필터 + 검색
  const filteredCylinders = useMemo(() => {
    return cylinders.filter(c => {
      const matchFilter =
        filter === "all" ||
        (filter === "warehouse" && c.locType === "warehouse") ||
        (filter === "customer" && c.locType === "customer");
      const q = searchQuery.toLowerCase();
      const matchSearch = !q ||
        c.id.toLowerCase().includes(q) ||
        c.item.includes(searchQuery) ||
        c.location.includes(searchQuery);
      return matchFilter && matchSearch;
    });
  }, [cylinders, filter, searchQuery]);

  // 용기 등록
  const handleAddCylinder = () => {
    if (!form.id.trim()) { setFormError("용기번호를 입력해주세요."); return; }
    if (cylinders.find(c => c.id === form.id.trim())) { setFormError("이미 등록된 용기번호입니다."); return; }
    setCylinders([...cylinders, { ...form, id: form.id.trim() }]);
    setForm(EMPTY_FORM);
    setFormError("");
    setIsAddModal(false);
  };

  // 창고 추가
  const handleAddWarehouse = () => {
    if (!newWHName.trim()) return;
    if (warehouses.includes(newWHName.trim())) { alert("이미 있는 창고명입니다."); return; }
    const name = newWHName.trim();
    setWarehouses([...warehouses, name]);
    setNewWHName("");
    setIsWHModal(false);
  };

  const outCount = cylinders.filter(c => c.locType === "customer").length;
  const whCount  = cylinders.filter(c => c.locType === "warehouse").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* ── 창고 카드 ── */}
      {warehouses.length === 0 && (
        <div style={{ padding: '20px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 12, fontSize: 14, color: '#795548', fontWeight: 600 }}>
          ⚠ 등록된 창고가 없습니다. 아래 "새 창고 추가" 버튼으로 창고를 먼저 등록해주세요.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 16 }}>
        {warehouses.map(wh => (
          <WarehouseCard key={wh} name={wh} stock={warehouseStocks[wh] ?? {}} gasItems={GAS_ITEMS} />
        ))}
        <div onClick={() => setIsWHModal(true)} style={whAddBtn}>
          <Plus size={22} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>새 창고 추가</span>
        </div>
      </div>

      {/* ── 용기 목록 ── */}
      <div style={tableSection}>

        {/* 툴바 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px 14px 20px", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>전체 용기 목록</h3>
            <span style={countBadge(whCount, "#185FA5")}>창고 {whCount}개</span>
            <span style={countBadge(outCount, "#DC2626")}>출고중 {outCount}개</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={searchBox}>
              <Search size={14} color="#aaa" />
              <input placeholder="용기번호·품목·위치 검색" style={searchInput} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <button onClick={() => { setIsAddModal(true); setFormError(""); setForm({ ...EMPTY_FORM, item: GAS_ITEMS[0] ?? "", location: warehouses[0] ?? "" }); }} style={addCylBtn}>
              <Plus size={15} /> 용기 등록
            </button>
          </div>
        </div>

        {/* 필터 탭 */}
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #eee", padding: "0 20px" }}>
          {[["all","전체"], ["warehouse","창고 보관중"], ["customer","거래처 출고중"]].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={filterTab(filter === val)}>{label}</button>
          ))}
        </div>

        {/* 테이블 */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f8f7f2" }}>
            <tr>
              <th style={thStyle}>용기번호</th>
              <th style={thStyle}>가스 종류</th>
              <th style={thStyle}>상태</th>
              <th style={thStyle}>현재 위치</th>
              <th style={thStyle}>충전기한</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>바코드</th>
            </tr>
          </thead>
          <tbody>
            {filteredCylinders.map(c => {
              const expiry = c.chargeExpiry ? new Date(c.chargeExpiry) : null;
              const today = new Date(); today.setHours(0,0,0,0);
              const daysLeft = expiry ? Math.ceil((expiry - today) / 86400000) : null;
              const expiryColor = daysLeft === null ? "#aaa" : daysLeft < 0 ? "#DC2626" : daysLeft <= 30 ? "#D97706" : "#059669";
              return (
                <tr key={c.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontWeight: 700 }}>{c.id}</td>
                  <td style={tdStyle}>{c.item}</td>
                  <td style={tdStyle}>
                    <span style={c.status === "실병" ? badgeFull : badgeEmpty}>{c.status}</span>
                  </td>
                  <td style={tdStyle}>
                    {c.locType === "warehouse"
                      ? <span style={locWarehouse}><Home size={12} /> {c.location}</span>
                      : <span style={locCustomer}><span style={{ fontSize: 12 }}>↗</span> {c.location} (출고중)</span>
                    }
                  </td>
                  <td style={{ ...tdStyle, color: expiryColor, fontWeight: daysLeft !== null && daysLeft <= 30 ? 700 : 400 }}>
                    {c.chargeExpiry
                      ? <>
                          {c.chargeExpiry}
                          {daysLeft !== null && (
                            <span style={{ fontSize: 11, marginLeft: 4 }}>
                              ({daysLeft < 0 ? `${Math.abs(daysLeft)}일 초과` : daysLeft === 0 ? "오늘 만료" : `D-${daysLeft}`})
                            </span>
                          )}
                        </>
                      : <span style={{ color: "#ccc" }}>-</span>
                    }
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={() => setBarcodeCyl(c)} style={barcodeBtnStyle}>
                      <Printer size={13} /> 출력
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredCylinders.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#aaa", fontSize: 14 }}>해당 용기가 없습니다</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── 바코드 모달 ── */}
      {barcodeCyl && (
        <div style={overlay}>
          <div style={{ ...modal, width: 360, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>바코드 출력</h3>
              <button onClick={() => setBarcodeCyl(null)} style={closeBtn}><X size={18} /></button>
            </div>

            {/* 바코드 렌더링 영역 */}
            <div ref={barcodeRef} style={{ display: 'inline-block', padding: '16px 20px', background: '#fff', border: '1px solid #e5e3db', borderRadius: 10 }}>
              <Code39Barcode value={barcodeCyl.id} />
            </div>

            <div style={{ marginTop: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>{barcodeCyl.id}</div>
              <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{barcodeCyl.item} · {barcodeCyl.location}</div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setBarcodeCyl(null)} style={{ ...modalBtn, background: '#eee', color: '#333' }}>닫기</button>
              <button onClick={() => handlePrintBarcode(barcodeCyl)} style={{ ...modalBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Printer size={16} /> 인쇄
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 용기 등록 모달 ── */}
      {isAddModal && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><PackagePlus size={20} color="#185FA5" /> 신규 용기 등록</h3>
              <button onClick={() => setIsAddModal(false)} style={closeBtn}><X size={18} /></button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="용기번호">
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="예: CYL-0601" value={form.id} onChange={e => { setForm({...form, id: e.target.value}); setFormError(""); }} />
                  <CameraScanner
                    color="#185FA5"
                    onScan={raw => { setForm(f => ({...f, id: raw.trim()})); setFormError(""); }}
                    buttonStyle={{ padding: "10px 12px", background: "#f0f4fb", color: "#185FA5", border: "1px solid #185FA533", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}
                    buttonContent={<><Camera size={14} /> 스캔</>}
                  />
                </div>
              </Field>
              <Field label="가스 종류">
                <select style={inputStyle} value={form.item} onChange={e => setForm({...form, item: e.target.value})}>
                  {GAS_ITEMS.map(i => <option key={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="충전 상태">
                <div style={{ display: "flex", gap: 10 }}>
                  {["실병", "공병"].map(s => (
                    <button key={s} onClick={() => setForm({...form, status: s})}
                      style={{ ...toggleBtn, background: form.status === s ? "#185FA5" : "#f0f0f0", color: form.status === s ? "#fff" : "#555" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="현재 위치">
                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                  {[["warehouse","창고"], ["customer","거래처"]].map(([val, label]) => (
                    <button key={val} onClick={() => setForm({...form, locType: val, location: val === "warehouse" ? warehouses[0] : CUSTOMERS[0]})}
                      style={{ ...toggleBtn, background: form.locType === val ? "#185FA5" : "#f0f0f0", color: form.locType === val ? "#fff" : "#555" }}>
                      {label}
                    </button>
                  ))}
                </div>
                {form.locType === "warehouse"
                  ? <select style={inputStyle} value={form.location} onChange={e => setForm({...form, location: e.target.value})}>
                      {warehouses.map(w => <option key={w}>{w}</option>)}
                    </select>
                  : <select style={inputStyle} value={form.location} onChange={e => setForm({...form, location: e.target.value})}>
                      {CUSTOMERS.map(c => <option key={c}>{c}</option>)}
                    </select>
                }
              </Field>
              <Field label="충전기한">
                <input
                  type="date"
                  style={inputStyle}
                  value={form.chargeExpiry}
                  onChange={e => setForm({...form, chargeExpiry: e.target.value})}
                />
              </Field>
            </div>

            {formError && <p style={{ color: "#DC2626", fontSize: 13, margin: "12px 0 0 0" }}>{formError}</p>}

            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              <button onClick={() => setIsAddModal(false)} style={{ ...modalBtn, background: "#eee", color: "#333" }}>취소</button>
              <button onClick={handleAddCylinder} style={modalBtn}>등록하기</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 창고 추가 모달 ── */}
      {isWHModal && (
        <div style={overlay}>
          <div style={{ ...modal, width: 300 }}>
            <h3 style={{ marginTop: 0 }}>새 창고 등록</h3>
            <input value={newWHName} onChange={e => setNewWHName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAddWarehouse()} placeholder="창고명 입력" style={inputStyle} autoFocus />
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setIsWHModal(false)} style={{ ...modalBtn, background: "#eee", color: "#333" }}>취소</button>
              <button onClick={handleAddWarehouse} style={modalBtn}>등록</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* 창고 카드 */
const WarehouseCard = ({ name, stock, gasItems = [] }) => {
  const totalFull  = Object.values(stock).reduce((s, v) => s + (v.실병 ?? 0), 0);
  const totalEmpty = Object.values(stock).reduce((s, v) => s + (v.공병 ?? 0), 0);

  return (
    <div style={whCard}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Home size={17} color="#185FA5" />
        <strong style={{ fontSize: 15 }}>{name}</strong>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <span style={totalFull < 0 ? negBadge : sumFull}>실병 합계 {totalFull}개{totalFull < 0 && ' (음수재고)'}</span>
        <span style={totalEmpty < 0 ? negBadge : sumEmpty}>공병 합계 {totalEmpty}개{totalEmpty < 0 && ' (음수재고)'}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {gasItems.length === 0
          ? <div style={{ fontSize: 13, color: '#aaa' }}>등록된 품목이 없습니다</div>
          : gasItems.map(item => {
              const s = stock[item] ?? { 실병: 0, 공병: 0 };
              const empty = s.실병 === 0 && s.공병 === 0;
              return (
                <div key={item} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", opacity: empty ? 0.3 : 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{item}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <span style={s.실병 < 0 ? negCell : cellFull}>실병 <b>{s.실병}</b></span>
                    <span style={s.공병 < 0 ? negCell : cellEmpty}>공병 <b>{s.공병}</b></span>
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
};

/* 폼 필드 래퍼 */
const Field = ({ label, children }) => (
  <div>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

/* 스타일 */
const whCard    = { background: "#fff", border: "1px solid #d3d1c7", borderRadius: 14, padding: 20 };
const whAddBtn  = { border: "2px dashed #d3d1c7", borderRadius: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#aaa", gap: 8, background: "#fff", minHeight: 140 };
const sumFull   = { background: "#e8f0fb", color: "#185FA5", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20 };
const sumEmpty  = { background: "#f3f4f6", color: "#555",    fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20 };
const cellFull  = { background: "#eef3fb", color: "#185FA5", fontSize: 12, padding: "3px 10px", borderRadius: 8, minWidth: 60, textAlign: "center" };
const cellEmpty = { background: "#f5f5f5", color: "#666",    fontSize: 12, padding: "3px 10px", borderRadius: 8, minWidth: 60, textAlign: "center" };
const negBadge  = { background: "#fef3c7", color: "#92400E", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20 };
const negCell   = { background: "#fef3c7", color: "#92400E", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 8, minWidth: 60, textAlign: "center" };

const tableSection = { background: "white", borderRadius: 12, border: "1px solid #d3d1c7", overflow: "hidden" };
const thStyle = { padding: "12px 16px", textAlign: "left", fontSize: 13, color: "#777", fontWeight: 600, borderBottom: "1px solid #eee" };
const tdStyle = { padding: "12px 16px", fontSize: 14 };

const badgeFull  = { background: "#dbeafe", color: "#1d4ed8", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 };
const badgeEmpty = { background: "#f3f4f6", color: "#555",    padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 };
const locWarehouse = { display: "inline-flex", alignItems: "center", gap: 4, color: "#185FA5", fontSize: 13 };
const locCustomer  = { display: "inline-flex", alignItems: "center", gap: 4, color: "#DC2626", fontSize: 13, fontWeight: 600 };

const searchBox  = { display: "flex", alignItems: "center", background: "#f8f7f2", padding: "7px 12px", borderRadius: 8, border: "1px solid #d3d1c7" };
const searchInput = { border: "none", outline: "none", background: "transparent", marginLeft: 8, fontSize: 13, width: 180 };
const addCylBtn  = { display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" };

const filterTab = active => ({ padding: "10px 18px", background: "none", border: "none", borderBottom: active ? "3px solid #185FA5" : "3px solid transparent", color: active ? "#185FA5" : "#888", fontWeight: active ? 700 : 400, fontSize: 13, cursor: "pointer" });
const countBadge = (n, color) => ({ background: `${color}15`, color, fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20 });

const overlay  = { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 };
const modal    = { background: "white", padding: 28, borderRadius: 16, width: 380 };
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #d3d1c7", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fcfbf7" };
const toggleBtn  = { flex: 1, padding: "9px", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" };
const modalBtn   = { flex: 1, padding: "11px", background: "#185FA5", color: "white", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 14 };
const closeBtn   = { background: "none", border: "none", cursor: "pointer", color: "#aaa", display: "flex", padding: 4 };

const barcodeBtnStyle = { display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: '#f0f4fb', color: '#185FA5', border: '1px solid #185FA533', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' };


// ── Code39 바코드 (라이브러리 없이 SVG로 직접 렌더링) ──
const C39 = {
  '0':'000110100','1':'100100001','2':'001100001','3':'101100000',
  '4':'000110001','5':'100110000','6':'001110000','7':'000100101',
  '8':'100100100','9':'001100100','A':'100001001','B':'001001001',
  'C':'101001000','D':'000011001','E':'100011000','F':'001011000',
  'G':'000001101','H':'100001100','I':'001001100','J':'000011100',
  'K':'100000011','L':'001000011','M':'101000010','N':'000010011',
  'O':'100010010','P':'001010010','Q':'000000111','R':'100000110',
  'S':'001000110','T':'000010110','U':'110000001','V':'011000001',
  'W':'111000000','X':'010010001','Y':'110010000','Z':'011010000',
  '-':'010000101','.':'110000100',' ':'011000100','$':'010101000',
  '/':'010100010','+':'010001010','%':'000101010','*':'010010100',
};

const Code39Barcode = ({ value, barHeight = 60, n = 2 }) => {
  const w = n * 3;
  const text = `*${value.toUpperCase()}*`;
  const rects = [];
  let x = 10;

  for (let ci = 0; ci < text.length; ci++) {
    const pat = C39[text[ci]];
    if (!pat) continue;
    for (let pi = 0; pi < 9; pi++) {
      const bw = pat[pi] === '1' ? w : n;
      if (pi % 2 === 0) rects.push(<rect key={`${ci}-${pi}`} x={x} y={0} width={bw} height={barHeight} fill="#000" />);
      x += bw;
    }
    x += n; // inter-character gap
  }

  const total = x + 10;
  return (
    <svg width={total} height={barHeight + 22}>
      <rect width={total} height={barHeight + 22} fill="white" />
      {rects}
      <text x={total / 2} y={barHeight + 15} textAnchor="middle" fontSize={12} fontFamily="monospace" fill="#333">{value}</text>
    </svg>
  );
};

export default InventoryPanel;
